import styled from '@emotion/styled';
import type { ExtractedAnswerData } from '@trinity/chat/types/extracted-answers.api';

const Value = styled.span`
  font-size: 14px;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 4px;
  background: #f0f0f0;
  font-size: 13px;
`;

const RankItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
`;

const RankNumber = styled.span`
  font-weight: 700;
  min-width: 20px;
`;

type Props = {
  answer: ExtractedAnswerData;
};

export function AnswerValueDisplay({ answer }: Props): React.ReactElement {
  if (answer.confidence === 'not_found') {
    return <Value style={{ fontStyle: 'italic', color: '#999' }}>{answer.notFoundReason ?? 'Not found'}</Value>;
  }

  switch (answer.answerType) {
    case 'free_text':
      return <Value>{answer.value ?? '—'}</Value>;

    case 'single_select':
      return (
        <Value>
          {answer.value ?? '—'}
          {answer.otherDetail ? ` (${answer.otherDetail})` : ''}
        </Value>
      );

    case 'multi_select':
      return (
        <div>
          {answer.values.length === 0
            ? <Value>—</Value>
            : answer.values.map((v) => <Chip key={v}>{v}</Chip>)}
          {answer.otherDetail ? <Value> ({answer.otherDetail})</Value> : null}
        </div>
      );

    case 'yes_no':
      return (
        <Value>
          {answer.value == null ? '—' : answer.value ? 'Yes' : 'No'}
          {answer.rationale ? ` — ${answer.rationale}` : ''}
        </Value>
      );

    case 'numeric':
      return (
        <Value>
          {answer.value ?? '—'}
          {answer.unit ? ` ${answer.unit}` : ''}
        </Value>
      );

    case 'numeric_range':
      return <Value>{answer.value ?? '—'}</Value>;

    case 'ranked_list':
      return (
        <div>
          {answer.rankedItems.length === 0
            ? <Value>—</Value>
            : answer.rankedItems
                .sort((a, b) => a.rank - b.rank)
                .map((item) => (
                  <RankItem key={item.rank}>
                    <RankNumber>{item.rank}.</RankNumber>
                    <span>{item.label}</span>
                  </RankItem>
                ))}
        </div>
      );

    case 'likert':
      return (
        <Value>
          {answer.value ?? '—'}
          {answer.rationale ? ` — ${answer.rationale}` : ''}
        </Value>
      );
  }
}
